import React, { useState, useEffect, useCallback } from 'react';
import './AdminStudentRegistration.css';
import { isSuperAdmin, getBranchId, fetchWithAuth } from '../../utils/auth';
import { API_URL } from '../../config';
import {
  searchStudents, listAcademicYears, listGrades, listRegistrations,
  lookupRegistration, updateRegistrationGrade,
} from './studentRegistrationApi';

// The Evaluator's whole job: find students sitting in the placeholder
// "Evaluation" grade (not yet assigned, or up for promotion pending an
// exam) and move them into whichever real grade they've earned. Grade is
// the only thing this screen ever writes — matches the backend, which only
// exposes PUT /registrations/<id>/grade to this permission.
const EVALUATION_GRADE_NAME = 'Evaluation';

const AdminGradeEvaluation = () => {
  const superAdmin = isSuperAdmin();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(getBranchId() || 1);

  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [grades, setGrades] = useState([]);
  const evaluationGrade = grades.find(g => g.name.toLowerCase() === EVALUATION_GRADE_NAME.toLowerCase());

  // Queue is the default landing view -- search is tucked into its own tab
  // so it's not competing for attention above the fold on every visit.
  const [view, setView] = useState('queue'); // 'queue' | 'search'

  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState([]);
  const [related, setRelated] = useState([]);
  const [searching, setSearching] = useState(false);

  // Accordion, not a separate panel below the list or a separate page: the
  // clicked row expands in place with an editable sub-row directly under
  // it, so there's nothing to scroll to find, and only one record is ever
  // open for editing at a time (selectedStudent doubles as "which id is
  // expanded" for both the queue and search tables).
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [gradeSelectValue, setGradeSelectValue] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    listAcademicYears().then(({ ok, data }) => {
      if (ok) {
        setYears(data.academic_years || []);
        const current = (data.academic_years || []).find(y => y.is_current);
        setYearId(current ? current.id : (data.academic_years[0]?.id ?? null));
      }
    });
    listGrades(true).then(({ ok, data }) => { if (ok) setGrades(data.grades || []); });
  }, []);

  const loadQueue = useCallback(async () => {
    if (!yearId || !evaluationGrade) { setQueue([]); return; }
    setQueueLoading(true);
    const { ok, data } = await listRegistrations(yearId, evaluationGrade.id, branchId);
    if (ok) setQueue(data.registrations || []);
    setQueueLoading(false);
  }, [yearId, evaluationGrade, branchId]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const doSearch = async () => {
    if (!searchValue.trim()) return;
    setSearching(true);
    setError('');
    setSelectedStudent(null);
    const { ok, data } = await searchStudents(searchValue.trim(), yearId);
    if (ok) {
      setResults(data.results || []);
      setRelated(data.related || []);
    } else {
      setError(data.error || 'Search failed.');
    }
    setSearching(false);
  };

  const closePanel = () => {
    setSelectedStudent(null);
    setExistingRegistration(null);
    setComment('');
    setError('');
    setMessage('');
  };

  const selectStudent = async (student) => {
    // Clicking the already-expanded row closes it instead of re-opening it.
    if (selectedStudent?.id === student.id) { closePanel(); return; }
    setSelectedStudent(student);
    setError('');
    setMessage('');
    setComment('');
    setExistingRegistration(null);
    if (!yearId) return;
    const { ok, data } = await lookupRegistration(student.id, yearId);
    if (ok && data.registration) {
      setExistingRegistration(data.registration);
      setGradeSelectValue(data.registration.grade_id);
    } else {
      setExistingRegistration(null);
      setError('This student is not registered for the selected year yet.');
    }
  };

  const selectFromQueue = (reg) => {
    selectStudent({ id: reg.student_id, ...reg.student });
  };

  const handleUpdateGrade = async () => {
    if (!gradeSelectValue || !existingRegistration) return;
    if (!comment.trim()) {
      setError('Enter a comment (score/notes) describing this evaluation before saving.');
      return;
    }
    setSaving(true);
    setError('');
    let { ok, data } = await updateRegistrationGrade(existingRegistration.id, Number(gradeSelectValue), comment.trim());
    if (ok && data.needs_confirmation) {
      const confirmed = window.confirm(
        `${selectedStudent?.first_name || 'This student'} already has book(s) marked issued for their ` +
        `current grade: ${data.issued_books.join(', ')}.\n\n` +
        `Changing grade will remove these entries -- the physical books may need to be collected back ` +
        `since they were issued for the wrong grade. Continue?`
      );
      if (!confirmed) { setSaving(false); return; }
      ({ ok, data } = await updateRegistrationGrade(existingRegistration.id, Number(gradeSelectValue), comment.trim(), true));
    }
    if (ok && data.registration) {
      setExistingRegistration(data.registration);
      setComment('');
      setMessage('Grade updated.');
      loadQueue();
    } else {
      setError(data.error || 'Failed to update grade.');
    }
    setSaving(false);
  };

  // The expandable sub-row rendered directly under whichever row is
  // selected, in both the queue table and the search table.
  const renderEditPanel = (colSpan) => (
    <tr>
      <td colSpan={colSpan} className="sr-expand-cell">
        <div className="sr-inline-row" style={{ justifyContent: 'space-between' }}>
          <h4 style={{ margin: 0 }}>
            {selectedStudent.first_name} {selectedStudent.last_name} <span className="sr-badge-muted">(ID: {selectedStudent.id})</span>
          </h4>
          <button className="btn-sm" onClick={closePanel}>Close</button>
        </div>

        {existingRegistration ? (
          <>
            <div className="sr-info-grid">
              <div className="form-row faq-form-row">
                <label>Date of Birth</label>
                <span>{selectedStudent?.date_of_birth || '—'}</span>
              </div>
              <div className="form-row faq-form-row">
                <label>Phone</label>
                <span>{selectedStudent?.phone || '—'}</span>
              </div>
              <div className="form-row faq-form-row">
                <label>Email</label>
                <span>{selectedStudent?.primary_email || '—'}</span>
              </div>
              <div className="form-row faq-form-row">
                <label>Current Grade</label>
                <span>{existingRegistration?.grade_name || '—'}</span>
              </div>
            </div>

            <div className="form-row faq-form-row">
              <label>Assign Grade</label>
              <select value={gradeSelectValue} onChange={e => setGradeSelectValue(e.target.value)}>
                {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>

            {existingRegistration.notes && (
              <div className="form-row faq-form-row">
                <label>Prior Notes</label>
                <span style={{ whiteSpace: 'pre-wrap' }}>{existingRegistration.notes}</span>
              </div>
            )}

            <div className="form-row faq-form-row">
              <label>Evaluation Comment *</label>
              <textarea
                placeholder="Score, observations, or reason for this grade assignment…"
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={3}
                style={{ minWidth: '280px', fontFamily: 'inherit' }}
              />
            </div>
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '-8px' }}>
              Required — added to this student's notes, never replaces what's already there.
            </p>

            <div className="form-actions">
              <button
                className="btn-primary"
                onClick={handleUpdateGrade}
                disabled={saving || !comment.trim()}
              >
                {saving ? 'Saving…' : 'Update Grade'}
              </button>
            </div>
          </>
        ) : (
          <p style={{ color: '#94a3b8' }}>Not registered for the selected year.</p>
        )}
      </td>
    </tr>
  );

  return (
    <div className="faq-manager-container">
      <div className="faq-manager-header">
        <div>
          <h2>Grade Evaluation</h2>
          <p className="faq-manager-subtitle">Assign a grade to students awaiting evaluation or promotion.</p>
        </div>
        <div className="faq-manager-header-right">
          {superAdmin && branches.length > 0 && (
            <select value={branchId} onChange={e => setBranchId(Number(e.target.value))} className="branch-select">
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <select value={yearId || ''} onChange={e => setYearId(Number(e.target.value))} className="branch-select">
            {years.map(y => <option key={y.id} value={y.id}>{y.year}{y.is_current ? ' (current)' : ''}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <div className="sr-success-banner">{message}</div>}

      <div className="sr-inline-row">
        <button className={view === 'queue' ? 'btn-primary' : 'btn-secondary'} onClick={() => { setView('queue'); closePanel(); }}>
          Awaiting Evaluation
        </button>
        <button className={view === 'search' ? 'btn-primary' : 'btn-secondary'} onClick={() => { setView('search'); closePanel(); }}>
          Search
        </button>
      </div>

      {view === 'queue' && (
        <div className="form-card">
          <h3 className="faq-form-title">Awaiting Evaluation</h3>
          {!evaluationGrade ? (
            <p style={{ color: '#94a3b8' }}>No "{EVALUATION_GRADE_NAME}" grade is configured yet.</p>
          ) : queueLoading ? (
            <p style={{ color: '#94a3b8' }}>Loading…</p>
          ) : queue.length === 0 ? (
            <p style={{ color: '#94a3b8' }}>No students currently awaiting evaluation.</p>
          ) : (
            <div className="sr-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Name</th><th>Grade</th>
                    <th className="sr-col-hide-mobile">Date of Birth</th>
                    <th className="sr-col-hide-mobile">Phone</th>
                    <th className="sr-col-hide-mobile">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map(reg => (
                    <React.Fragment key={reg.id}>
                      <tr className="sr-row-clickable" onClick={() => selectFromQueue(reg)}>
                        <td>{reg.student_id}</td>
                        <td className="sr-name-link">{reg.student?.first_name} {reg.student?.last_name}</td>
                        <td>{reg.grade_name}</td>
                        <td className="sr-col-hide-mobile">{reg.student?.date_of_birth || '—'}</td>
                        <td className="sr-col-hide-mobile">{reg.student?.phone || '—'}</td>
                        <td className="sr-col-hide-mobile">{reg.student?.primary_email || '—'}</td>
                      </tr>
                      {selectedStudent?.id === reg.student_id && renderEditPanel(6)}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === 'search' && (
        <div className="form-card">
          <div className="sr-inline-row">
            <input
              type="text" placeholder="Search by ID, name, phone, or email…" value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              style={{ minWidth: '260px' }}
            />
            <button className="btn-primary" onClick={doSearch} disabled={searching}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {results.length > 0 && (
            <div className="sr-table-scroll" style={{ marginTop: '12px' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Name</th>
                    <th className="sr-col-hide-mobile">Phone</th>
                    <th className="sr-col-hide-mobile">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(s => (
                    <React.Fragment key={s.id}>
                      <tr className="sr-row-clickable" onClick={() => selectStudent(s)}>
                        <td>{s.id}</td>
                        <td className="sr-name-link">{s.first_name} {s.last_name}</td>
                        <td className="sr-col-hide-mobile">{s.phone || '—'}</td>
                        <td className="sr-col-hide-mobile">{s.primary_email || '—'}</td>
                      </tr>
                      {selectedStudent?.id === s.id && renderEditPanel(4)}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {related.length > 0 && (
            <>
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>Related (same phone/email — likely siblings):</p>
              <div className="sr-table-scroll">
                <table className="admin-table">
                  <tbody>
                    {related.map(s => (
                      <React.Fragment key={s.id}>
                        <tr className="sr-row-clickable" onClick={() => selectStudent(s)}>
                          <td className="sr-name-link">{s.first_name} {s.last_name}</td>
                        </tr>
                        {selectedStudent?.id === s.id && renderEditPanel(1)}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminGradeEvaluation;
